import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { EXPLORATION } from '@lootboy/lib/adPlacementTypes';

import DailyTiles from '~/components/DailyTasksView/DailyTiles';
import NotificationsSwitch from '~/components/DailyTasksView/NotificationsSwitch';
import PopupMinimalistic from '~/components/PopupMinimalistic';

import useBooleanState from '~/hooks/useBooleanState';
import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import useInterval from '~/hooks/useInterval';
import useDailyQuestsHook from '~/redux/hooks/useDailyQuestsHook';
import { dailySelector } from '~/redux/selectors';

import DailyHeader from './DailyHeader';
import DailyInfo from './DailyInfo';
import { ExplorationTask } from './TaskWrapper';

import style from './DailyTasksView.module.scss';

const taskMapper = {
  [EXPLORATION]: ExplorationTask,
};

function DailyTasksView() {
  useDailyQuestsHook();
  useDocumentTitle(I18n.t('DailyView_bc'));

  const { items: quests, lastSuccessfulFetch } = useSelector(dailySelector);

  const {
    interval: { backgroundGradient, description, popupGradient } = {},
    progress: { lastClaimAt },
    rewards,
    loading,
    notificationsEnabled: dailyNotificationsEnabled,
  } = useInterval();

  const [siteNotificationsEnabled, setSiteNotificationsEnabled] =
    useBooleanState();

  useEffect(() => {
    window.OneSignal.push(() => {
      // eslint-disable-next-line max-nested-callbacks
      window.OneSignal.isPushNotificationsEnabled().then((isEnabled) => {
        if (isEnabled) {
          setSiteNotificationsEnabled.setTrue();
        }
      });
    });
  });

  return (
    <PopupMinimalistic
      loading={loading || !lastSuccessfulFetch}
      style={{ background: `linear-gradient(${backgroundGradient})` }}
      buttonClassName={style.dailyTasksModalCloseButton}
      className={classNames(style.dailyTasks, 'v2')}
      containerClassName={style.dailyTasksModalContainer}
      wrapperClassName={style.popupWrapper}
    >
      <DailyHeader description={description} />
      <DailyInfo popupGradient={popupGradient} />
      <DailyTiles
        rewards={rewards}
        popupGradient={popupGradient}
        lastClaimAt={lastClaimAt}
        dailyNotificationsEnabled={dailyNotificationsEnabled}
        siteNotificationsEnabled={siteNotificationsEnabled}
      />
      {siteNotificationsEnabled && (
        <NotificationsSwitch
          dailyNotificationsEnabled={dailyNotificationsEnabled}
        />
      )}
      <div className={style.questsContainer}>
        {quests.map(({ type, _id, ...rest }) => {
          const Task = taskMapper[type];
          return (
            Task && (
              <Task
                key={_id}
                _id={_id}
                {...rest}
              />
            )
          );
        })}
      </div>
    </PopupMinimalistic>
  );
}

export default DailyTasksView;
